/**
 * ChartTypeMenu — a COMPACT dropdown (one small icon button) for switching a
 * card's chart type, on both the card AND its drilldown (founder 2026-06-13:
 * "a small thing they can click… like a dropdown, not taking up a bunch of
 * room… choose on both views and save it"). The choice — and the rolling-
 * average toggle — persist per card key, so the card and its drill stay in
 * sync and survive reloads.
 *
 * Chart-type suite (research-grounded — line/area for trends, bars/column for
 * categories, stacked/100% for composition, donut/treemap for share, plus
 * step, table, number). Each surface passes the SUBSET that's honest for its
 * data shape.
 */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type ChartType =
  | "line" | "area" | "column" | "step"          // single time series
  | "stacked" | "grouped" | "stacked100"          // two series over time
  | "bars" | "donut" | "treemap"                  // categorical / share
  | "table" | "number";                           // raw / collapsed

export const CHART_LABEL: Record<ChartType, string> = {
  line: "Line", area: "Area", column: "Column", step: "Step line",
  stacked: "Stacked bars", grouped: "Grouped bars", stacked100: "100% stacked",
  bars: "Bars", donut: "Donut", treemap: "Treemap",
  table: "Table", number: "Number",
};

/** Trailing rolling average — the trend line founder wants. null until the
 *  window fills, so the smoothed line starts where it's honest. */
export function rollingAverage(values: number[], window: number): Array<number | null> {
  if (window < 2) return values.slice();
  const out: Array<number | null> = [];
  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    sum += values[i] ?? 0;
    if (i >= window) sum -= values[i - window] ?? 0;
    out.push(i >= window - 1 ? sum / window : null);
  }
  return out;
}

/** Sensible rolling window from the label cadence: daily→7, monthly→3. */
export function rollingWindow(labels: string[]): number {
  const daily = labels.some((l) => /^\d{4}-\d{2}-\d{2}$/.test(l));
  return daily ? 7 : 3;
}

/** Persisted chart preference for a card key — shared by card + drill. Generic
 *  over the surface's own chart-type vocabulary (TrendChart kinds, RankedBars
 *  kinds, drill kinds), so each can persist its choice under the same key. */
export function useChartPref(chartKey: string | undefined, fallback: string): {
  type: string; setType: (t: string) => void; avg: boolean; setAvg: (v: boolean) => void;
} {
  const read = (): { type: string; avg: boolean } => {
    // Rolling average defaults ON (founder 2026-06-13: "rolling averages by
    // default and they have to click it off") — only an explicit "0" turns it off.
    if (!chartKey || typeof localStorage === "undefined") return { type: fallback, avg: true };
    try {
      const t = localStorage.getItem(`dd-chart-${chartKey}`);
      const a = localStorage.getItem(`dd-chartavg-${chartKey}`);
      return { type: t ?? fallback, avg: a === null ? true : a === "1" };
    } catch { return { type: fallback, avg: true }; }
  };
  const [state, setState] = useState(read);
  const setType = (t: string): void => {
    setState((s) => ({ ...s, type: t }));
    if (chartKey) { try { localStorage.setItem(`dd-chart-${chartKey}`, t); } catch { /* in-memory */ } }
  };
  const setAvg = (v: boolean): void => {
    setState((s) => ({ ...s, avg: v }));
    if (chartKey) { try { localStorage.setItem(`dd-chartavg-${chartKey}`, v ? "1" : "0"); } catch { /* in-memory */ } }
  };
  return { type: state.type, setType, avg: state.avg, setAvg };
}

export interface ChartMenuOption { value: string; label: string }

export function ChartTypeMenu({ value, options, onChange, avg, onToggleAvg, avgApplies }: {
  value: string;
  options: ChartMenuOption[];
  onChange: (t: string) => void;
  /** Rolling-average overlay state — omit the toggle when not applicable. */
  avg?: boolean;
  onToggleAvg?: (v: boolean) => void;
  /** Whether the rolling-avg toggle should show for the CURRENT type. */
  avgApplies?: boolean;
}): React.ReactElement {
  const current = options.find((o) => o.value === value)?.label ?? value;
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const MENU_W = 168;

  // Position the menu from the trigger's viewport rect — it's PORTALED to body
  // so card overflow:hidden / stacking contexts can't clip or bury it (Codex).
  const place = (): void => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 4, left: Math.max(8, r.right - MENU_W) });
  };
  useEffect(() => {
    if (!open) return;
    place();
    const onDown = (e: MouseEvent): void => {
      const t = e.target as Node;
      if (!btnRef.current?.contains(t) && !menuRef.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") setOpen(false); };
    const reclose = (): void => setOpen(false); // scroll/resize → dismiss (avoid drift)
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", reclose, true);
    window.addEventListener("resize", reclose);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", reclose, true);
      window.removeEventListener("resize", reclose);
    };
  }, [open]);

  const showAvgToggle = onToggleAvg !== undefined && avgApplies;
  return (
    <span style={{ display: "inline-block" }} onClick={(e) => e.stopPropagation()}>
      <button ref={btnRef} type="button" onClick={() => setOpen((o) => !o)} title={`Chart type: ${current}`} aria-label="Change chart type" aria-haspopup="menu" aria-expanded={open}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--body)", borderRadius: 6, padding: "2px 6px", fontSize: 11, cursor: "pointer", lineHeight: 1.4 }}>
        <ChartGlyph /> <span style={{ fontWeight: 600 }}>{current}</span> <span style={{ fontSize: 8, opacity: 0.7 }}>▾</span>
      </button>
      {open && pos && createPortal(
        <div ref={menuRef} role="menu" onClick={(e) => e.stopPropagation()}
          style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 200, width: MENU_W, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "var(--shadow-pop)", padding: 4 }}>
          {options.map((opt) => (
            <button key={opt.value} role="menuitemradio" aria-checked={value === opt.value} onClick={() => { onChange(opt.value); setOpen(false); }}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, width: "100%", border: "none", background: value === opt.value ? "var(--surface-2)" : "transparent", color: "var(--text)", borderRadius: 6, padding: "6px 9px", fontSize: 12.5, cursor: "pointer", textAlign: "left", fontWeight: value === opt.value ? 600 : 400 }}>
              {opt.label} {value === opt.value && <span style={{ color: "var(--accent)" }}>✓</span>}
            </button>
          ))}
          {showAvgToggle && (
            <>
              <div style={{ borderTop: "1px solid var(--border)", margin: "4px 0" }} />
              <button role="menuitemcheckbox" aria-checked={!!avg} onClick={() => onToggleAvg?.(!avg)}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, width: "100%", border: "none", background: "transparent", color: "var(--text)", borderRadius: 6, padding: "6px 9px", fontSize: 12.5, cursor: "pointer", textAlign: "left" }}>
                Rolling-average trend <span style={{ color: avg ? "var(--accent)" : "var(--faint)" }}>{avg ? "✓ on" : "off"}</span>
              </button>
            </>
          )}
        </div>,
        document.body,
      )}
    </span>
  );
}

function ChartGlyph(): React.ReactElement {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" style={{ flexShrink: 0 }}>
      <rect x="0.5" y="6" width="2.4" height="5.5" rx="0.5" fill="currentColor" />
      <rect x="4.8" y="3" width="2.4" height="8.5" rx="0.5" fill="currentColor" />
      <rect x="9.1" y="0.7" width="2.4" height="10.8" rx="0.5" fill="currentColor" />
    </svg>
  );
}
